import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, CalendarDays, CheckCircle2, Clock } from 'lucide-react'
import { supabase } from '../supabase'
import { useUser } from '../UserContext'

function normaliseDateValue(value) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString().split('T')[0]
}

function getDeliveryDueDate(delivery) {
  if (delivery.delivery_date) return normaliseDateValue(delivery.delivery_date)

  const customFields = delivery.custom_fields || {}
  const preferredKeys = ['due_date', 'delivery_date', 'date_of_activation', 'activation_date', 'deadline', 'date']

  for (const key of preferredKeys) {
    if (customFields[key]) return normaliseDateValue(customFields[key])
  }

  const matchingKey = Object.keys(customFields).find(key =>
    key.toLowerCase().includes('date') ||
    key.toLowerCase().includes('deadline') ||
    key.toLowerCase().includes('due')
  )

  return matchingKey ? normaliseDateValue(customFields[matchingKey]) : null
}

function isComplete(status) {
  return status === 'delivered' || status === 'approved'
}

function toDateKey(date) {
  return date.toISOString().split('T')[0]
}

function getMonthDays(viewDate) {
  const year = viewDate.getFullYear()
  const month = viewDate.getMonth()
  const firstDay = new Date(year, month, 1)
  const start = new Date(firstDay)
  const firstWeekday = (start.getDay() + 6) % 7

  start.setDate(start.getDate() - firstWeekday)

  return Array.from({ length: 42 }, (_, index) => {
    const d = new Date(start)
    d.setDate(start.getDate() + index)

    return {
      date: d,
      key: toDateKey(d),
      isCurrentMonth: d.getMonth() === month,
      isToday: toDateKey(d) === toDateKey(new Date())
    }
  })
}

export default function Calendar() {
  const { orgId } = useUser()

  const [loading, setLoading] = useState(true)
  const [deliveries, setDeliveries] = useState([])
  const [deals, setDeals] = useState([])
  const [viewDate, setViewDate] = useState(() => new Date())
  const [selectedDate, setSelectedDate] = useState(() => toDateKey(new Date()))

  useEffect(() => {
    fetchCalendar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  async function fetchCalendar() {
    if (!orgId) return

    setLoading(true)

    const [
      { data: deliveryData, error: deliveryError },
      { data: dealData, error: dealError }
    ] = await Promise.all([
      supabase
        .from('deliveries')
        .select('id, name, status, delivery_date, custom_fields, org_id, rights(name, deal_id, deals(id, name, clients(name)))')
        .eq('org_id', orgId),

      supabase
        .from('deals')
        .select('id, name, status, end_date, org_id, clients(name)')
        .eq('org_id', orgId)
    ])

    if (deliveryError) console.error(deliveryError)
    if (dealError) console.error(dealError)

    setDeliveries(deliveryData || [])
    setDeals(dealData || [])
    setLoading(false)
  }

  const events = useMemo(() => {
    const deliveryEvents = deliveries
      .map(delivery => {
        const date = getDeliveryDueDate(delivery)
        if (!date) return null

        return {
          id: `delivery-${delivery.id}`,
          date,
          type: 'delivery',
          title: delivery.name || delivery.rights?.name || 'Delivery',
          subtitle: delivery.rights?.deals?.name || 'Deal',
          client: delivery.rights?.deals?.clients?.name || '',
          status: delivery.status || 'pending',
          complete: isComplete(delivery.status)
        }
      })
      .filter(Boolean)

    const dealEvents = deals
      .filter(deal => deal.end_date)
      .map(deal => ({
        id: `deal-${deal.id}`,
        date: normaliseDateValue(deal.end_date),
        type: 'deal',
        title: deal.name,
        subtitle: deal.clients?.name || 'Client',
        client: deal.clients?.name || '',
        status: deal.status || 'active',
        complete: false
      }))
      .filter(event => event.date)

    return [...deliveryEvents, ...dealEvents].sort((a, b) => new Date(a.date) - new Date(b.date))
  }, [deliveries, deals])

  const eventsByDate = useMemo(() => {
    return events.reduce((map, event) => {
      if (!map[event.date]) map[event.date] = []
      map[event.date].push(event)
      return map
    }, {})
  }, [events])

  const monthDays = useMemo(() => getMonthDays(viewDate), [viewDate])
  const selectedEvents = eventsByDate[selectedDate] || []

  const monthEvents = events.filter(event => {
    const d = new Date(event.date)
    return d.getFullYear() === viewDate.getFullYear() && d.getMonth() === viewDate.getMonth()
  })

  const pendingCount = monthEvents.filter(event => event.type === 'delivery' && !event.complete).length
  const completedCount = monthEvents.filter(event => event.type === 'delivery' && event.complete).length
  const dealCount = monthEvents.filter(event => event.type === 'deal').length

  function goPreviousMonth() {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
  }

  function goNextMonth() {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
  }

  function goToday() {
    const today = new Date()
    setViewDate(today)
    setSelectedDate(toDateKey(today))
  }

  if (loading) {
    return (
      <div className="hv-page" style={{ color: 'var(--hv-text-muted)', paddingTop: '60px' }}>
        Loading calendar...
      </div>
    )
  }

  return (
    <div className="hv-page">
      <style>{`
        .calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 18px;
          margin-bottom: 24px;
        }

        .calendar-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .calendar-shell {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 360px;
          gap: 18px;
        }

        .calendar-card {
          background: #fff;
          border: 1px solid var(--hv-border);
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(15, 21, 35, 0.05);
        }

        .calendar-main {
          padding: 22px;
        }

        .calendar-monthbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 18px;
        }

        .calendar-month-title {
          font-size: 18px;
          font-weight: 850;
          color: var(--hv-text-primary);
          letter-spacing: -0.4px;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, minmax(0, 1fr));
          border: 1px solid var(--hv-border-light);
          border-radius: 14px;
          overflow: hidden;
        }

        .calendar-weekday {
          padding: 12px 10px;
          background: #F8FAFC;
          border-right: 1px solid var(--hv-border-light);
          border-bottom: 1px solid var(--hv-border-light);
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.08em;
          color: var(--hv-text-muted);
          text-transform: uppercase;
        }

        .calendar-weekday:nth-child(7) {
          border-right: none;
        }

        .calendar-day {
          min-height: 128px;
          padding: 10px;
          border: 0;
          border-right: 1px solid var(--hv-border-light);
          border-bottom: 1px solid var(--hv-border-light);
          background: #fff;
          cursor: pointer;
          text-align: left;
          font-family: inherit;
          transition: background 0.15s ease, box-shadow 0.15s ease;
        }

        .calendar-day:nth-child(7n) {
          border-right: none;
        }

        .calendar-day:nth-last-child(-n + 7) {
          border-bottom: none;
        }

        .calendar-day:hover {
          background: #F8FAFC;
        }

        .calendar-day--muted {
          background: #FBFCFE;
        }

        .calendar-day--selected {
          box-shadow: inset 0 0 0 2px #4F46E5;
          background: #F8F7FF;
        }

        .calendar-day-number {
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 800;
          color: var(--hv-text-primary);
          margin-bottom: 8px;
        }

        .calendar-day--muted .calendar-day-number {
          color: var(--hv-text-muted);
        }

        .calendar-day-number--today {
          background: #4F46E5;
          color: #fff;
        }

        .calendar-event-pill {
          display: block;
          width: 100%;
          border-radius: 8px;
          padding: 6px 7px;
          margin-bottom: 6px;
          font-size: 11px;
          font-weight: 750;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .calendar-event-pill--delivery {
          background: #EEF2FF;
          color: #4338CA;
        }

        .calendar-event-pill--deal {
          background: #F0FDF4;
          color: #15803D;
        }

        .calendar-event-pill--done {
          background: #ECFDF5;
          color: #047857;
        }

        .calendar-more {
          font-size: 11px;
          color: var(--hv-text-muted);
          font-weight: 700;
          padding-left: 3px;
        }

        .calendar-side {
          padding: 22px;
          align-self: start;
          position: sticky;
          top: 24px;
        }

        .calendar-stat-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin-bottom: 18px;
        }

        .calendar-stat {
          border: 1px solid var(--hv-border-light);
          border-radius: 12px;
          padding: 12px;
        }

        .calendar-stat-value {
          font-size: 22px;
          font-weight: 850;
          color: var(--hv-text-primary);
          line-height: 1;
        }

        .calendar-stat-label {
          font-size: 11px;
          color: var(--hv-text-muted);
          margin-top: 6px;
          font-weight: 700;
        }

        .calendar-selected-title {
          font-size: 15px;
          font-weight: 850;
          color: var(--hv-text-primary);
          margin: 18px 0 12px;
        }

        .calendar-event-card {
          border: 1px solid var(--hv-border-light);
          border-radius: 12px;
          padding: 14px;
          margin-bottom: 10px;
          background: #fff;
        }

        .calendar-event-type {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 800;
          padding: 4px 8px;
          border-radius: 999px;
          margin-bottom: 10px;
        }

        .calendar-event-type--delivery {
          background: #EEF2FF;
          color: #4338CA;
        }

        .calendar-event-type--deal {
          background: #F0FDF4;
          color: #15803D;
        }

        .calendar-event-title {
          font-size: 13px;
          font-weight: 850;
          color: var(--hv-text-primary);
          margin-bottom: 3px;
        }

        .calendar-event-subtitle {
          font-size: 12px;
          color: var(--hv-text-muted);
        }

        @media (max-width: 1100px) {
          .calendar-shell {
            grid-template-columns: 1fr;
          }

          .calendar-side {
            position: static;
          }
        }
      `}</style>

      <div className="calendar-header">
        <div>
          <h1 className="hv-page-title">Calendar</h1>
          <p className="hv-page-subtitle">View deal end dates and delivery deadlines across your organisation.</p>
        </div>

        <div className="calendar-actions">
          <button className="hv-btn hv-btn-secondary" onClick={goToday}>Today</button>
          <button className="hv-btn hv-btn-secondary" onClick={goPreviousMonth}>
            <ChevronLeft size={16} />
          </button>
          <button className="hv-btn hv-btn-secondary" onClick={goNextMonth}>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="calendar-shell">
        <div className="calendar-card calendar-main">
          <div className="calendar-monthbar">
            <div className="calendar-month-title">
              {viewDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', fontWeight: 700 }}>
              {monthEvents.length} item{monthEvents.length === 1 ? '' : 's'} this month
            </div>
          </div>

          <div className="calendar-grid">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
              <div key={day} className="calendar-weekday">{day}</div>
            ))}

            {monthDays.map(day => {
              const dayEvents = eventsByDate[day.key] || []
              const visibleEvents = dayEvents.slice(0, 3)

              return (
                <button
                  key={day.key}
                  type="button"
                  className={[
                    'calendar-day',
                    !day.isCurrentMonth ? 'calendar-day--muted' : '',
                    selectedDate === day.key ? 'calendar-day--selected' : ''
                  ].filter(Boolean).join(' ')}
                  onClick={() => setSelectedDate(day.key)}
                >
                  <div className={`calendar-day-number ${day.isToday ? 'calendar-day-number--today' : ''}`}>
                    {day.date.getDate()}
                  </div>

                  {visibleEvents.map(event => (
                    <div
                      key={event.id}
                      className={[
                        'calendar-event-pill',
                        event.type === 'deal' ? 'calendar-event-pill--deal' : 'calendar-event-pill--delivery',
                        event.complete ? 'calendar-event-pill--done' : ''
                      ].filter(Boolean).join(' ')}
                    >
                      {event.title}
                    </div>
                  ))}

                  {dayEvents.length > 3 && (
                    <div className="calendar-more">+{dayEvents.length - 3} more</div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <aside className="calendar-card calendar-side">
          <div className="calendar-stat-grid">
            <div className="calendar-stat">
              <div className="calendar-stat-value">{pendingCount}</div>
              <div className="calendar-stat-label">Pending</div>
            </div>
            <div className="calendar-stat">
              <div className="calendar-stat-value">{completedCount}</div>
              <div className="calendar-stat-label">Delivered</div>
            </div>
            <div className="calendar-stat">
              <div className="calendar-stat-value">{dealCount}</div>
              <div className="calendar-stat-label">Deals</div>
            </div>
          </div>

          <div className="calendar-selected-title">
            {new Date(selectedDate).toLocaleDateString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </div>

          {selectedEvents.length === 0 ? (
            <div style={{ color: 'var(--hv-text-muted)', fontSize: '13px' }}>
              No deadlines on this date.
            </div>
          ) : (
            selectedEvents.map(event => (
              <div key={event.id} className="calendar-event-card">
                <div className={`calendar-event-type calendar-event-type--${event.type}`}>
                  {event.type === 'deal' ? <CalendarDays size={13} /> : event.complete ? <CheckCircle2 size={13} /> : <Clock size={13} />}
                  {event.type === 'deal' ? 'Deal end date' : event.complete ? 'Delivered' : 'Delivery deadline'}
                </div>

                <div className="calendar-event-title">{event.title}</div>
                <div className="calendar-event-subtitle">
                  {event.subtitle}{event.client ? ` · ${event.client}` : ''}
                </div>
              </div>
            ))
          )}
        </aside>
      </div>
    </div>
  )
}
